/* eslint-disable rxjs/no-exposed-subjects */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, delay, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { bufferDebounceTime } from 'common/rxjs/operators';
import { connectToNotificationChannel, connectToNotificationChannelSuccess, createNotificationChannel, initializeModule, notificationArrived, notificationChannelCreated, notificationChannelError, subscribeToNotifications, subscribeToNotificationsSuccess } from '../actions/notification.actions';
import { selectRetryCount } from '../selectors/notification.selectors';
import { WebSocketSubject } from 'rxjs/webSocket';
import { flatten, uniq } from 'lodash';
import { NotificationMessage } from 'common/store/genesys-cloud/notifications/models/notification.model';
import { NotificationApiService } from '../services/notification-api.service';
import { of } from 'rxjs';
import { NotificationBarService as NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { NotificationType } from 'common/common/constants/notification-type.enum';

@Injectable()
export class GenesysCloudNotificationEffects {
  static readonly MinWebsocketRetryDelayMillis = 1000;
  static readonly MaxWebsocketRetryDelayMillis = 15_000;
  static readonly NotificationReconnectingId = 'gc-notification-channel-reconnecting';
  static readonly NotificationReconnectedId = 'gc-notification-channel-reconnected';
  static readonly NotificationReconnectedCloseAfterMillis = 2000;

  private readonly OmittedNotificationTopics = new Set<string>([
    'channel.metadata' // websocket heartbeat
  ]);

  readonly init$ = createEffect(() => this.actions$.pipe(
    ofType(initializeModule),
    map(() => createNotificationChannel())
  ));

  readonly createNotificationChannel$ = createEffect(() => this.actions$.pipe(
    ofType(createNotificationChannel),
    mergeMap(() => this.notificationApi.createNotificationChannel$()),
    map(channel => notificationChannelCreated({ channel }))
  ));

  readonly subscribeToNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(subscribeToNotifications),
    bufferDebounceTime(0),
    map(actions => ({
      userId: actions[0].userId,
      channelId: actions[0].channelId,
      topics: uniq(flatten(actions.map(a => a.topics)))
    })),
    mergeMap(({ userId, channelId, topics }) => this.notificationApi.subscribeToChannel$(userId, channelId, ...topics)),
    map(topics => subscribeToNotificationsSuccess({ topics }))
  ));

  readonly connectToNotificationChannel$ = createEffect(() => this.actions$.pipe(
    ofType(notificationChannelCreated),
    map(({ channel }) => connectToNotificationChannel({ channel, isReconnect: false }))
  ));

  readonly openAndListenNotifications$ = createEffect(() => this.actions$.pipe(
    ofType(connectToNotificationChannel),
    switchMap(({ channel, isReconnect }) => this.createWebSocketSubject(channel.connectUri, isReconnect).pipe(
      filter(notification => !this.OmittedNotificationTopics.has(notification.topicName)),
      map(notification => notificationArrived({ notification })),
      catchError(() => of(notificationChannelError({ channel })))
    ))
  ));

  readonly handleWebsocketCloseErrors$ = createEffect(() => this.actions$.pipe(
    ofType(notificationChannelError),
    concatLatestFrom(() => this.store.select(selectRetryCount)),
    switchMap(([{ channel }, retryCount]) => of(connectToNotificationChannel({ channel, isReconnect: true })).pipe(
        delay(this.getRetryDelayMillis(retryCount))
      )
    )
  ));

  readonly showReconnectingNotificationOnConnectionError$ = createEffect(() => this.actions$.pipe(
    ofType(notificationChannelError),
    tap(() => this.notificationBarService.removeNotification(GenesysCloudNotificationEffects.NotificationReconnectedId)),
    tap(() => this.notificationBarService.addNotification({
      id: GenesysCloudNotificationEffects.NotificationReconnectingId,
      content: 'gcNotifications.reconnecting',
      isClosable: false
    }))
  ), { dispatch: false });

  readonly showReconnectedNotificationOnConnectionRestored$ = createEffect(() => this.actions$.pipe(
    ofType(connectToNotificationChannelSuccess),
    filter(({ isReconnect }) => isReconnect),
    tap(() => this.notificationBarService.removeNotification(GenesysCloudNotificationEffects.NotificationReconnectingId)),
    tap(() => this.notificationBarService.addNotification({
      id: GenesysCloudNotificationEffects.NotificationReconnectedId,
      content: 'gcNotifications.reconnected',
      type: NotificationType.Success,
      isClosable: true,
      autoCloseMillis: GenesysCloudNotificationEffects.NotificationReconnectedCloseAfterMillis
    }))
  ), { dispatch: false });

  constructor(private readonly actions$: Actions,
              private readonly notificationApi: NotificationApiService,
              private readonly store: Store,
              private readonly notificationBarService: NotificationBarService
  ) {
  }

  createWebSocketSubject(url: string, isReconnect: boolean): WebSocketSubject<NotificationMessage<any, any>> {
    return new WebSocketSubject<NotificationMessage<any, any>>({
      url,
      openObserver: {
        next: () => this.store.dispatch(connectToNotificationChannelSuccess({ isReconnect }))
      }
    });
  }

  getRetryDelayMillis(retryCount: number): number {
    return Math.min(
      GenesysCloudNotificationEffects.MinWebsocketRetryDelayMillis * Math.pow(2, retryCount),
      GenesysCloudNotificationEffects.MaxWebsocketRetryDelayMillis
    );
  }
}
